<template>
  <!--  need to keep the class knw_widget_button to be backwards compatible
  for some custom customer button implementations >:(
  -->
  <button
    ref="moin_button_bubble"
    class="moin_widget_button knw_widget_button"
    :aria-label="t('widgetbutton.button')"
  >
    <div class="moin_bubble_icon" aria-hidden="true">
      <IconChatBubble />
    </div>
    <transition-showavatar name="kw-avatar-transition">
      <div v-if="showAvatar" class="avatar-wrapper" aria-hidden="true">
        <Avatar :image="avatarUrl" width="64px" height="64px" />
      </div>
    </transition-showavatar>
  </button>
</template>

<script setup lang="ts">
import { getBestColorContrast } from "@/common/colors";
import Avatar from "@/components/common/Avatar.vue";
import { useI18n } from "@/composables/useI18n";
import IconChatBubble from "@/svg/IconChatBubble.vue";
import transitionShowavatar from "./transition-showavatar.vue";
const { t } = useI18n();

import { onMounted } from "vue";
import { useForceRepaint } from "@/composables/useForceRepaint";
import { templateRef } from "@vueuse/core";

defineProps<{
  showAvatar: boolean;
  avatarUrl: string;
}>();

const bubble = templateRef("moin_button_bubble");
const { triggerRepaint } = useForceRepaint(bubble);

const calcContrastColor = () => {
  if (!bubble.value) return "white";
  const color = getComputedStyle(bubble.value).backgroundColor;
  return getBestColorContrast(color);
};

onMounted(() => {
  if (!bubble.value) return;
  // calc the contrast color for the svg
  // to ensure it is always visible
  // and not the same color as the background
  // because the button will be always tested in a11y test tools
  const color = calcContrastColor();
  bubble.value.style.setProperty("--fallbackSVGColor", color);
  triggerRepaint();
});
</script>

<style scoped lang="scss">
.knw_widget_button_wrapper {
  pointer-events: all;
  -webkit-tap-highlight-color: transparent;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;

  z-index: 1000;

  &.animate {
    transition: bottom 0.5s ease-in-out, right 0.5s ease-in-out;
  }

  @media (min-width: $kw-widget-breakpoint) {
    /* <<< MEDIUM <<< */
    margin: 0;
    /* bottom: 40px;
    right: 40px; */
  }
}

.moin_widget_button {
  --fallbackSVGColor: white;

  max-width: 60px;
  max-height: 60px;
  padding: 0;
  width: 60px;
  height: 60px;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  align-items: center;
  justify-items: center;

  border: none;
  // TODO WHAT IS THE DEFAULT COLOR?
  background: var(--kw-widget-widget_button-background, white);

  border-radius: var(--kw-widget-global-borderradius-shape, 50%);
  box-shadow: 0px 0px 15px $kw-default-shadow-color-dark;
  @include default-hover-scale;

  svg {
    width: 34px;
    height: 32px;
    fill: white;
    fill: var(--kw-widget-widget_button-textcolor, var(--fallbackSVGColor, red));
  }

  @include default-focus-unknown-bg;
  .avatar-wrapper {
    border: 3px solid white; /* Ensure always border on tab focus */
  }
}

.moin_bubble_icon {
  grid-column: 1;
  grid-row: 1;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  justify-self: center;
}

.avatar-wrapper {
  grid-column: 1;
  grid-row: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  // border-radius: 50%;
  border-radius: var(--kw-widget-global-borderradius-shape);
  border: 3px solid white;
  width: 100%;
  height: 100%;
  cursor: pointer;
  overflow: hidden;
  z-index: 1;

  @media (min-width: $kw-teaser-breakpoint) {
    // prevent colored background from bleeding through the edges
    transform: scale(1.01);
  }

  .avatar {
    background-color: #ffffff;
    position: relative;
    z-index: 0;

    img {
      width: 100%;
      height: 100%;
    }

    svg {
      border: none;
    }
  }
}
</style>
